import { zodResolver } from '@hookform/resolvers/zod';
import { statics } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { createQBooksCustomer, createQBooksVendor } from 'pages/requestForm/actions/qbooks';
import {
    EditableQBooksCustomer,
    EditableQBooksVendor,
    isQBooksCustomer,
} from 'pages/requestForm/data/qbooks/EditableQBooksCounterparty';
import { useGetRecognizedOcrAttachment } from 'pages/requestForm/hooks/useGetRecognizedOcrAttachment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { QBooksCustomerCreationPopupData } from 'pages/requestForm/reducers/page/activePopup/qBooksCustomerCreationPopupReducer';
import { QBooksContactCreationPopupData } from 'pages/requestForm/reducers/page/activePopup/qBooksVendorCreationPopupReducer';
import { getActivePopup, requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { getDefaultValues } from './QBooksCounterpartyCreationPopupContent.hooks.helpers';
import { schema } from './QBooksCounterpartyCreationPopupContent.schema';
import { FormValues } from './QBooksCounterpartyCreationPopupContent.types';

export const useQBooksCounterpartyCreationForm = () => {
    const request = useSelector(requestSelector);
    const popupData = useSelector<QBooksCustomerCreationPopupData | QBooksContactCreationPopupData>(getActivePopup);
    const dispatch = useDispatch();

    const isSalesInvoice = request.integrationCode === domain.IntegrationCode.QBooksInvoice;

    const initContact = useMemo(
        () =>
            isSalesInvoice
                ? {
                      ...(popupData as QBooksCustomerCreationPopupData).initCustomer,
                      sameAsBillingAddress: true,
                  }
                : (popupData as QBooksContactCreationPopupData).initVendor,
        [isSalesInvoice, popupData]
    );

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const form = useForm<FormValues>({
        defaultValues: getDefaultValues(initContact, request.companyCurrency, ocrData),
        resolver: zodResolver(schema),
    });

    const handleSubmit = form.handleSubmit(({ currencyId, ...values }) => {
        if ('sameAsBillingAddress' in values) {
            const sameAsBillingAddress = values.sameAsBillingAddress;

            delete values.sameAsBillingAddress;

            if (sameAsBillingAddress) {
                values.shippingAddress = values.billingAddress;
            }
        }

        const counterparty: EditableQBooksCustomer | EditableQBooksVendor = values;

        if (currencyId) {
            counterparty.currency = statics.currency.findCurrencyByCode(currencyId);
        }

        isQBooksCustomer(counterparty)
            ? dispatch(createQBooksCustomer(counterparty))
            : dispatch(createQBooksVendor(counterparty));
    });

    return {
        form,
        handleSubmit,
        isSalesInvoice,
    };
};
